import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { SiteShell } from "@/components/site/SiteShell";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { createAgencyCheckoutSession } from "@/lib/agency.functions";
import { buildHead } from "@/lib/seo";

const GOLD = "#C8A84B";

export const Route = createFileRoute("/agency-bundle")({
  head: () =>
    buildHead({
      locale: "en",
      title: "Agency Bundle · 5 GEO Audits at $149 each · ShugertGEO",
      description:
        "Self-serve 5-audit agency bundle. White-label, you QA & deliver. Pay once or subscribe monthly.",
      path: "/agency-bundle",
      altPath: "/agency-bundle",
      noindex: true,
    }),
  component: AgencyBundlePage,
});

type Step = "plan" | "auth" | "pay";
type Kind = "one_time" | "monthly";

function AgencyBundlePage() {
  const router = useRouter();
  const createSession = useServerFn(createAgencyCheckoutSession);
  const [kind, setKind] = useState<Kind>("one_time");
  const [step, setStep] = useState<Step>("plan");
  const [email, setEmail] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setEmail(data.session?.user.email ?? null);
      setHydrated(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setEmail(session?.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const fetchClientSecret = useCallback(async () => {
    const res = await createSession({
      data: {
        kind,
        environment: getStripeEnvironment(),
        return_origin: window.location.origin,
      },
    });
    if ("error" in res) throw new Error(res.error);
    return res.clientSecret;
  }, [createSession, kind]);

  return (
    <SiteShell>
      <PaymentTestModeBanner />
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-5xl px-6 py-10 sm:py-14">
          <p
            className="text-[10px] font-medium uppercase tracking-[0.22em]"
            style={{ color: GOLD }}
          >
            Agency Bundle · 5 audits
          </p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-foreground sm:text-4xl">
            $149 per audit, in bundles of 5
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            White-label Pro audits you launch from a dashboard. We run the
            pipeline; you handle QA and deliver to your clients.
          </p>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-6 py-10 sm:py-14">
          {step === "plan" && (
            <div className="grid gap-6">
              <div className="grid gap-4 md:grid-cols-2">
                <KindCard
                  selected={kind === "one_time"}
                  onSelect={() => setKind("one_time")}
                  title="One-time bundle"
                  price="$745"
                  suffix="one-time · 5 credits"
                  description="Pay once for 5 credits. Valid 12 months."
                  bullets={[
                    "5 Pro-equivalent audits",
                    "Credits valid 12 months",
                    "Buy more bundles anytime",
                  ]}
                />
                <KindCard
                  selected={kind === "monthly"}
                  onSelect={() => setKind("monthly")}
                  title="Monthly subscription"
                  price="$745"
                  suffix="/ month · auto-renew"
                  description="5 fresh credits each month. Cancel anytime."
                  badge="Best for active agencies"
                  bullets={[
                    "5 fresh Pro audits / month",
                    "Unused credits roll over while sub is active",
                    "Manage or cancel from dashboard",
                  ]}
                />
              </div>
              <div className="rounded-2xl border border-border bg-card/30 p-5 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">What you get:</strong>{" "}
                  Pro-equivalent audits launched from a private dashboard.
                  Each report is generated white-label (your brand on cover &
                  footer), <strong className="text-foreground">we don't
                  QA or talk to your clients</strong>. You review and deliver.
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setStep(email ? "pay" : "auth")}
                  className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {step === "auth" && (
            <div className="mx-auto max-w-md rounded-2xl border border-border bg-card/30 p-8 text-center">
              <p
                className="text-[10px] font-medium uppercase tracking-[0.18em]"
                style={{ color: GOLD }}
              >
                One last step
              </p>
              <h2 className="mt-2 font-display text-xl font-semibold text-foreground">
                {hydrated && !email
                  ? "Sign in or create an account"
                  : "Loading…"}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Credits and your dashboard live on your account. Sign in (or
                create one) and you'll come right back here to pay.
              </p>
              {hydrated && !email && (
                <div className="mt-5 flex flex-col gap-3">
                  <Link
                    to="/agency-login"
                    search={{ redirect: "/agency-bundle" }}
                    className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90"
                  >
                    Sign in / Create account →
                  </Link>
                  <button
                    type="button"
                    onClick={() => setStep("plan")}
                    className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                  >
                    ← Back
                  </button>
                </div>
              )}
              {email && (
                <div className="mt-5 flex flex-col gap-3">
                  <button
                    onClick={() => setStep("pay")}
                    className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90"
                  >
                    Continue to payment →
                  </button>
                </div>
              )}
            </div>
          )}

          {step === "pay" && email && (
            <div className="grid gap-6">
              <div className="rounded-2xl border border-border bg-card/30 p-4 text-sm text-foreground">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <p className="font-medium">
                      {kind === "monthly"
                        ? "Agency Monthly, $745 / month"
                        : "Agency Bundle, $745 one-time"}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      5 audit credits · billed to {email}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep("plan")}
                    className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                  >
                    ← Change plan
                  </button>
                </div>
              </div>
              <StripeEmbeddedCheckout fetchClientSecret={fetchClientSecret} />
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}

function KindCard(props: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  suffix: string;
  description: string;
  bullets: string[];
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onSelect}
      aria-pressed={props.selected}
      className="group relative flex h-full flex-col rounded-2xl border bg-card/30 p-6 text-left transition-all"
      style={{
        borderColor: props.selected ? GOLD : "var(--color-border)",
        boxShadow: props.selected ? `0 0 0 1px ${GOLD}` : undefined,
      }}
    >
      {props.badge && (
        <span
          className="absolute -top-3 left-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]"
          style={{ background: GOLD, color: "#0a0a0a" }}
        >
          {props.badge}
        </span>
      )}
      <h3 className="font-display text-xl font-semibold text-foreground">
        {props.title}
      </h3>
      <p className="mt-3 font-display text-3xl font-semibold text-foreground">
        {props.price}
      </p>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {props.suffix}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">{props.description}</p>
      <ul className="mt-5 flex-1 space-y-2 text-sm text-foreground/90">
        {props.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span
              aria-hidden
              className="mt-[7px] inline-block size-1.5 shrink-0 rounded-full"
              style={{ background: GOLD }}
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div
        className="mt-5 inline-flex items-center justify-center rounded-full border px-4 py-2 text-xs font-medium uppercase tracking-[0.18em]"
        style={{
          borderColor: props.selected ? GOLD : "var(--color-border)",
          color: props.selected ? GOLD : "var(--color-foreground)",
        }}
      >
        {props.selected ? "Selected" : "Select"}
      </div>
    </button>
  );
}